import * as z from "zod";
import { creditNoteDocumentDto } from "../documents/credit-note-document";
import { pageOf, pagination, version } from "../index";
import { claimOutcomes, claimStatuses, claimTypes, MAX_CLAIM_FILES, ticketRequesters } from "./enums";

// Claims of the customers (M15, FR-M15-01…04, B-55, B-75, ADR-225): the customer opens one in Frontrow on an order
// and a line of it, or the Customer Service opens it for them; Zendesk is the master of the ticket (I-17, I-32), and
// Nexxt keeps the history by customer, SKU and type.

/**
 * `openClaim` (FR-M15-01): `claimId` is the caller's key, a uuid the client makes when the form opens: the same claim
 * again answers the one already open, and never opens a second ticket. The photos and files are uploaded first into
 * the `claims` folder of files; a defective or faulty product needs at least one (B-55).
 */
export const openClaimInput = z.object({
  claimId: z.uuid(),
  soId: z.uuid(),
  soLineId: z.uuid().optional(),
  type: z.enum(claimTypes),
  description: z.string().trim().min(1).max(8000).optional(),
  /** Units of the line the claim is about: only with a line. */
  quantity: z.number().int().min(1).max(1_000_000).optional(),
  fileIds: z.array(z.uuid()).max(MAX_CLAIM_FILES).default([]),
});
export type OpenClaimInput = z.input<typeof openClaimInput>;

/** A claim by its id: `getClaim`, and the path of the routes of one claim. */
export const claimParams = z.object({ claimId: z.uuid() });
export type GetClaimInput = z.input<typeof claimParams>;

/**
 * The Customer Service sends a claim to Zendesk again when its ticket could not be opened (I-17 refused or given up):
 * the same key, so Zendesk never opens two. `version` is the one the client read (409 otherwise).
 */
export const retryClaimTicketInput = z.object({ claimId: z.uuid(), version });
export type RetryClaimTicketInput = z.input<typeof retryClaimTicketInput>;
/** The gateway routes split the input: the id in the path, the rest in the body. */
export const retryClaimTicketBody = retryClaimTicketInput.omit({ claimId: true });

/**
 * B-75: the order that replaces the goods of a claim, made by the Customer Service in sales: an order of the same
 * customer, never the one claimed. `version` is the one the client read (409 otherwise).
 */
export const linkClaimReplacementInput = z.object({ claimId: z.uuid(), version, replacementSoId: z.uuid() });
export type LinkClaimReplacementInput = z.input<typeof linkClaimReplacementInput>;
export const linkClaimReplacementBody = linkClaimReplacementInput.omit({ claimId: true });

/**
 * B-54 point 3, B-75 (ADR-230): Finance approves the credit note of a claim Zendesk closed with one, on its line and
 * units, and documents asks Business Central for it (I-04 `credit_note`). `version` is the one the client read (409
 * otherwise).
 */
export const approveClaimCreditInput = z.object({ claimId: z.uuid(), version });
export type ApproveClaimCreditInput = z.input<typeof approveClaimCreditInput>;
export const approveClaimCreditBody = approveClaimCreditInput.omit({ claimId: true });

/**
 * A photo or a file of a claim, downloaded by whoever reads the claim (ADR-084 point 5, ADR-228): always as an
 * attachment, never a URL to the bucket.
 */
export const claimAttachmentParams = z.object({ claimId: z.uuid(), fileId: z.uuid() });
export type DownloadClaimAttachmentInput = z.input<typeof claimAttachmentParams>;

const claimFilters = {
  type: z.enum(claimTypes).optional(),
  status: z.enum(claimStatuses).optional(),
  outcome: z.enum(claimOutcomes).optional(),
  salesOrder: z.uuid().optional(),
};

const historyFilters = { ...claimFilters, customer: z.uuid().optional(), sku: z.uuid().optional() };

/** The claims, the latest first: the history by customer, SKU and type (FR-M15-04), for Tomorrow. */
export const listClaimsInput = z.object({ ...historyFilters, ...pagination({ defaultSize: 25, maxSize: 100 }) });
export type ListClaimsInput = z.input<typeof listClaimsInput>;

/** How many claims the list has with the same filters, and when the one untouched for longest was updated (ADR-086). */
export const countClaimsInput = z.object(historyFilters);
export type CountClaimsInput = z.input<typeof countClaimsInput>;

/** The claims of the customer's own orders, the latest first, in Frontrow. */
export const listCustomerClaimsInput = z.object({ ...claimFilters, ...pagination({ defaultSize: 25, maxSize: 100 }) });
export type ListCustomerClaimsInput = z.input<typeof listCustomerClaimsInput>;

/** A photo or a file of a claim, as its claim keeps it: whoever sees the claim sees it (ADR-084 point 5). */
export const claimAttachmentDto = z.object({
  fileId: z.uuid(),
  filename: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
});

/** A claim, as the lists show it. */
export const claimSummaryDto = z
  .object({
    id: z.uuid(),
    type: z.enum(claimTypes),
    status: z.enum(claimStatuses),
    outcome: z.enum(claimOutcomes).nullable(),
    salesOrder: z.object({ id: z.uuid(), soNumber: z.string() }),
    line: z
      .object({ id: z.uuid(), lineNo: z.number().int(), skuId: z.uuid().nullable(), skuCode: z.string().nullable() })
      .nullable(),
    quantity: z.number().int().nullable(),
    customer: z.object({ id: z.uuid(), name: z.string() }),
    /** The ticket on Zendesk, once it has one. */
    ticketId: z.string().nullable(),
    /**
     * The order that replaces the goods. `fromClaim`: the Customer Service made it from this claim (B-75, ADR-251), and
     * no other order can be linked in its place.
     */
    replacementOrder: z.object({ id: z.uuid(), soNumber: z.string(), fromClaim: z.boolean() }).nullable(),
    attachmentCount: z.number().int(),
    openedAt: z.iso.datetime(),
    openedBy: z.string().nullable(),
    /** When Zendesk last changed the ticket, as I-32 said. */
    ticketUpdatedAt: z.iso.datetime().nullable(),
    /** Why its ticket could not be opened, for Tomorrow alone: the Customer Service sends it again. */
    ticketError: z.string().nullable().optional(),
    version: z.number().int(),
  })
  .meta({ id: "ClaimSummary" });
export type ClaimSummary = z.output<typeof claimSummaryDto>;

export const claimPageDto = pageOf(claimSummaryDto);

/** The credit note of a claim (ADR-230), for Tomorrow alone: who in Finance approved it, when, and its document. */
export const claimCreditDto = z.object({
  approvedBy: z.string().nullable(),
  approvedAt: z.iso.datetime(),
  document: creditNoteDocumentDto.nullable(),
});

/** A claim with what the customer wrote and its photos and files. */
export const claimDto = claimSummaryDto
  .extend({
    description: z.string().nullable(),
    attachments: z.array(claimAttachmentDto),
    /** Its credit note, once Finance approved it; for Tomorrow alone. */
    credit: claimCreditDto.nullable().optional(),
    /**
     * Whether Finance can approve its credit note now (ADR-230): Zendesk closed it with one, it names a line and its
     * units, and none was approved. `claim.credit` is still needed; for Tomorrow alone.
     */
    canApproveCredit: z.boolean().optional(),
    /**
     * Who its ticket was asked for on Zendesk (B-78, ADR-252); `customer_service` when the order has no buyer and the
     * customer no `buyer` contact, and the Customer Service gets the answers (B-98). Null on a claim older than it;
     * for Tomorrow alone.
     */
    ticketRequester: z.enum(ticketRequesters).nullable().optional(),
  })
  .meta({ id: "Claim" });
export type ClaimDto = z.output<typeof claimDto>;
